import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eraser, Camera, PackageCheck, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, GradButton } from "@/components/erp";
import api, { errMsg } from "@/lib/api";

const ImzaAlani = ({ canvasRef, onDegis }) => {
  const ciz = useRef(false);
  useEffect(() => {
    const c = canvasRef.current;
    c.width = c.offsetWidth * 2; c.height = c.offsetHeight * 2;
    const ctx = c.getContext("2d");
    ctx.scale(2, 2); ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#111";
  }, [canvasRef]);
  const nokta = (e) => { const c = canvasRef.current; const r = c.getBoundingClientRect(); return [(e.clientX - r.left) * c.offsetWidth / r.width, (e.clientY - r.top) * c.offsetHeight / r.height]; };
  const bas = (e) => { e.preventDefault(); canvasRef.current.setPointerCapture(e.pointerId); ciz.current = true; const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...nokta(e)); };
  const hareket = (e) => { if (!ciz.current) return; const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(...nokta(e)); ctx.stroke(); onDegis(true); };
  return <canvas ref={canvasRef} data-testid="teslim-imza-canvas" onPointerDown={bas} onPointerMove={hareket} onPointerUp={() => { ciz.current = false; }} onPointerLeave={() => { ciz.current = false; }}
    className="w-full h-44 rounded-xl bg-white cursor-crosshair" style={{ touchAction: "none" }} />;
};

const sikistir = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const o = Math.min(1, 1280 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = img.width * o; c.height = img.height * o;
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(img.src);
    resolve(c.toDataURL("image/jpeg", 0.75));
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

export const TeslimDialog = ({ v, onClose, onSaved }) => {
  const canvasRef = useRef(null);
  const [imzali, setImzali] = useState(false);
  const [ad, setAd] = useState("");
  const [foto, setFoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cari, setCari] = useState(null);
  const [eposta, setEposta] = useState(false);
  useEffect(() => {
    api.get(`/sevkiyatlar/${v.id}`).then((r) => { setCari({ email: r.data.cari_email }); setEposta(!!r.data.cari_email && r.data.cari_teslim_eposta); });
  }, [v.id]);
  const temizle = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setImzali(false); };
  const fotoSec = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setFoto(await sikistir(f)); } catch { toast.error("Fotoğraf okunamadı"); }
  };
  const save = async () => {
    if (ad.trim().length < 2) return toast.error("Teslim alan kişinin adını girin");
    if (!imzali) return toast.error("Alıcının imzasını alın");
    setBusy(true);
    try {
      const { data } = await api.post(`/sevkiyatlar/${v.id}/teslim`, { teslim_alan: ad, imza: canvasRef.current.toDataURL("image/png"), foto, eposta_gonder: eposta });
      toast.success(`${v.no} teslim edildi${eposta ? ` · imzalı irsaliye ${cari.email} adresine gönderiliyor` : ""}`);
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-lg max-h-[94vh] overflow-y-auto erp-scroll" data-testid="teslim-dialog">
        <DialogHeader><DialogTitle className="font-head">Teslimat · {v.no} · {v.cari_unvan}</DialogTitle></DialogHeader>
        <Field label="Teslim alan ad soyad *"><Input data-testid="teslim-alan" className="bg-white/5" value={ad} onChange={(e) => setAd(e.target.value)} /></Field>
        <div>
          <div className="flex items-center justify-between mb-1.5"><span className="text-xs text-muted-foreground">İmza * (parmakla veya fareyle)</span>
            <button type="button" onClick={temizle} data-testid="teslim-imza-temizle" className="text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-white"><Eraser className="w-3.5 h-3.5" />Temizle</button></div>
          <ImzaAlani canvasRef={canvasRef} onDegis={setImzali} />
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1.5">Teslim fotoğrafı (isteğe bağlı)</div>
          {foto ? (
            <div className="relative"><img src={foto} alt="Teslim" className="w-full max-h-56 object-cover rounded-xl" data-testid="teslim-foto-onizleme" />
              <button onClick={() => setFoto(null)} data-testid="teslim-foto-kaldir" className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60"><X className="w-4 h-4" /></button></div>
          ) : (
            <label className="flex items-center justify-center gap-2 h-20 rounded-xl border border-dashed border-white/15 text-sm text-muted-foreground hover:text-white cursor-pointer" data-testid="teslim-foto-label">
              <Camera className="w-4 h-4" />Fotoğraf çek / seç<input data-testid="teslim-foto" type="file" accept="image/*" capture="environment" className="hidden" onChange={fotoSec} />
            </label>
          )}
        </div>
        <label className={`flex items-start gap-2.5 text-sm rounded-xl bg-white/[0.04] p-3 ${cari?.email ? "cursor-pointer" : "opacity-60"}`} data-testid="teslim-eposta-label">
          <Checkbox data-testid="teslim-eposta" disabled={!cari?.email} checked={eposta} onCheckedChange={(x) => setEposta(!!x)} className="mt-0.5" />
          <span>İmzalı irsaliyeyi müşteriye e-postayla gönder<span className="block text-xs text-muted-foreground">{cari?.email ? cari.email : "Carinin e-posta adresi tanımlı değil"}</span></span>
        </label>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-5 py-2 rounded-full border border-white/10 text-sm" data-testid="teslim-vazgec">Vazgeç</button>
          <GradButton onClick={save} disabled={busy} data-testid="teslim-kaydet"><PackageCheck className="w-4 h-4" />{busy ? "Kaydediliyor…" : "Teslimi kaydet"}</GradButton></div>
      </DialogContent>
    </Dialog>
  );
};
